import React from 'react';
import {
    FunctionDataType,
    FunctionDefinition,
    FunctionPortDefinition,
    getStringRepresentationFromType,
    typeAMatchesTypeB,
} from '@glueapp/graph-types';
import { ArrowRight, Check, X } from 'lucide-react';
import { cn } from '@/lib/utils';

interface TypeSignatureProps {
    func: FunctionDefinition;
    expectedType: FunctionDataType;
}

type PortStatus = 'match' | 'mismatch' | 'extra';

function getInputPortStatus(port: FunctionPortDefinition, expectedType: FunctionDataType): PortStatus {
    const expectedPortType = expectedType.inputTypes[port.name];
    if (!expectedPortType) {
        return 'extra'; // Port exists but isn't required
    }
    if (typeAMatchesTypeB(port.type, expectedPortType)) {
        return 'match';
    }
    return 'mismatch';
}

function getOutputPortStatus(port: FunctionPortDefinition, expectedType: FunctionDataType): PortStatus {
    const expectedPortType = expectedType.outputTypes[port.name];
    if (!expectedPortType) {
        return 'extra';
    }
    if (typeAMatchesTypeB(port.type, expectedPortType)) {
        return 'match';
    }
    return 'mismatch';
}

export const TypeSignatureDisplay: React.FC<TypeSignatureProps> = ({ func, expectedType }) => {
    return (
        <div className="mt-2 pt-2 border-t border-border/50 text-xs text-muted-foreground">
            <div className="flex items-start gap-2">
                {/* Inputs */}
                <div className="flex-1 min-w-0">
                    <div className="font-medium mb-1 text-foreground/70">Inputs</div>
                    <div className="space-y-0.5">
                        {func.inputPortDefinitions.length === 0 ? (
                            <span className="text-muted-foreground/50 italic">none</span>
                        ) : (
                            func.inputPortDefinitions.map(port => {
                                const status = getInputPortStatus(port, expectedType);
                                const expectedPortType = expectedType.inputTypes[port.name];

                                return (
                                    <div key={port.name} className="flex items-start gap-1">
                                        {status === 'match' && <Check className="w-3 h-3 text-green-500 flex-shrink-0 mt-0.5" />}
                                        {status === 'mismatch' && <X className="w-3 h-3 text-orange-500 flex-shrink-0 mt-0.5" />}
                                        <span
                                            className={cn(
                                                'break-words',
                                                status === 'match' && 'text-foreground/80',
                                                status === 'mismatch' && 'text-orange-600 dark:text-orange-400',
                                                status === 'extra' && 'text-muted-foreground/60',
                                            )}
                                        >
                                            {port.name}
                                        </span>
                                        <span className="text-muted-foreground/40 flex-shrink-0">:</span>
                                        <span
                                            className={cn(
                                                'break-words',
                                                status === 'mismatch'
                                                    ? 'text-orange-600 dark:text-orange-400 line-through'
                                                    : 'text-muted-foreground/60',
                                            )}
                                        >
                                            {getStringRepresentationFromType(port.type)}
                                        </span>
                                        {status === 'mismatch' && expectedPortType && (
                                            <span className="break-words text-green-600 dark:text-green-400">
                                                {getStringRepresentationFromType(expectedPortType)}
                                            </span>
                                        )}
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>

                {/* Arrow */}
                <div className="flex-shrink-0 pt-5">
                    <ArrowRight className="w-4 h-4 text-muted-foreground/40" />
                </div>

                {/* Outputs */}
                <div className="flex-1 min-w-0">
                    <div className="font-medium mb-1 text-foreground/70">Outputs</div>
                    <div className="space-y-0.5">
                        {func.outputPortDefinitions.length === 0 ? (
                            <span className="text-muted-foreground/50 italic">none</span>
                        ) : (
                            func.outputPortDefinitions.map(port => {
                                const status = getOutputPortStatus(port, expectedType);
                                const expectedPortType = expectedType.outputTypes[port.name];

                                return (
                                    <div key={port.name} className="flex items-start gap-1">
                                        {status === 'match' && <Check className="w-3 h-3 text-green-500 flex-shrink-0 mt-0.5" />}
                                        {status === 'mismatch' && <X className="w-3 h-3 text-orange-500 flex-shrink-0 mt-0.5" />}
                                        <span
                                            className={cn(
                                                'break-words',
                                                status === 'match' && 'text-foreground/80',
                                                status === 'mismatch' && 'text-orange-600 dark:text-orange-400',
                                                status === 'extra' && 'text-muted-foreground/60',
                                            )}
                                        >
                                            {port.name}
                                        </span>
                                        <span className="text-muted-foreground/40 flex-shrink-0">:</span>
                                        <span
                                            className={cn(
                                                'break-words',
                                                status === 'mismatch'
                                                    ? 'text-orange-600 dark:text-orange-400 line-through'
                                                    : 'text-muted-foreground/60',
                                            )}
                                        >
                                            {getStringRepresentationFromType(port.type)}
                                        </span>
                                        {status === 'mismatch' && expectedPortType && (
                                            <span className="break-words text-green-600 dark:text-green-400">
                                                {getStringRepresentationFromType(expectedPortType)}
                                            </span>
                                        )}
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
